import { useState, useEffect } from 'react'
import { contactAPI, settingsAPI } from '../../services/api'
import { settingsToMap } from '../../utils/format'

import { logger } from '@/utils/logger';

interface ContactSettings {
  email: string
  phone: string
  address: string
  hours1: string
  hours2: string
  hours3: string
}

const EMPTY_FORM = { name: '', email: '', phone: '', subject: '', message: '' }

export default function Contact() {
  const [formData, setFormData] = useState(EMPTY_FORM)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [contactSettings, setContactSettings] = useState<ContactSettings | null>(null)

  useEffect(() => {
    settingsAPI.getPublic().then((res) => {
      const s = settingsToMap(res.data)
      setContactSettings({
        email: s.contact_email || '',
        phone: s.contact_phone || '',
        address: s.contact_address || '',
        hours1: s.contact_hours_1 || '',
        hours2: s.contact_hours_2 || '',
        hours3: s.contact_hours_3 || '',
      })
    }).catch((err) => logger.error('Error loading contact settings:', err))
  }, [])

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess(false)
    setLoading(true)

    try {
      await contactAPI.send(formData)
      setSuccess(true)
      setFormData(EMPTY_FORM)
    } catch (err) {
      setError('Fehler beim Senden der Nachricht. Bitte versuchen Sie es später erneut.')
      logger.error('Contact form error:', err)
    } finally {
      setLoading(false)
    }
  }

  const hours = [contactSettings?.hours1, contactSettings?.hours2, contactSettings?.hours3]
    .filter(Boolean)
  const hasContactInfo =
    !!(contactSettings?.email || contactSettings?.phone || contactSettings?.address)

  return (
    <div
      className="wc-container wc-split-grid"
      style={{
        padding: '72px 32px',
        display: 'grid',
        gridTemplateColumns: '0.9fr 1.1fr',
        gap: 48,
        alignItems: 'start',
      }}
    >
      <div>
        <p className="wc-kicker">Kontakt</p>
        <h1 style={{ marginBottom: 16 }}>Wir helfen gerne weiter</h1>
        <p className="wc-muted" style={{ maxWidth: 420, fontSize: 16 }}>
          Haben Sie Fragen zu einer App oder einem Tarif? Schreiben Sie uns —
          Ihre Anfrage landet direkt in unserem Postfach im Portal.
        </p>

        {hasContactInfo && (
          <div className="wc-card wc-elev-sm" style={{ padding: 20, marginTop: 32 }}>
            <h2 className="wc-card-title">Kontaktinformationen</h2>
            <div
              className="wc-muted"
              style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14 }}
            >
              {contactSettings?.email && (
                <a href={`mailto:${contactSettings.email}`} className="wc-footer-link">
                  {contactSettings.email}
                </a>
              )}
              {contactSettings?.phone && <div>{contactSettings.phone}</div>}
              {contactSettings?.address && <div>{contactSettings.address}</div>}
            </div>
          </div>
        )}

        {hours.length > 0 && (
          <div className="wc-card wc-elev-sm" style={{ padding: 20, marginTop: 16 }}>
            <h2 className="wc-card-title">Öffnungszeiten</h2>
            <div
              className="wc-muted"
              style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14 }}
            >
              {hours.map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
          </div>
        )}
      </div>

      <form className="wc-card wc-elev-sm" style={{ padding: 24, gap: 16 }} onSubmit={handleSubmit}>
        {success && (
          <div className="wc-alert wc-alert-success" role="status">
            Vielen Dank für Ihre Nachricht! Wir melden uns schnellstmöglich bei Ihnen.
          </div>
        )}
        {error && (
          <div className="wc-alert wc-alert-error" role="alert">
            {error}
          </div>
        )}

        <div className="wc-field">
          <label htmlFor="name">Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            className="wc-input"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </div>

        <div className="wc-field">
          <label htmlFor="email">E-Mail *</label>
          <input
            type="email"
            id="email"
            name="email"
            className="wc-input"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>

        <div className="wc-field">
          <label htmlFor="phone">Telefon</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            className="wc-input"
            value={formData.phone}
            onChange={handleChange}
          />
        </div>

        <div className="wc-field">
          <label htmlFor="subject">Betreff *</label>
          <input
            type="text"
            id="subject"
            name="subject"
            className="wc-input"
            value={formData.subject}
            onChange={handleChange}
            required
          />
        </div>

        <div className="wc-field">
          <label htmlFor="message">Nachricht *</label>
          <textarea
            id="message"
            name="message"
            className="wc-input"
            rows={6}
            value={formData.message}
            onChange={handleChange}
            required
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="wc-btn wc-btn-primary wc-btn-block"
        >
          {loading ? 'Senden…' : 'Nachricht senden'}
        </button>
      </form>
    </div>
  )
}
